import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dimmer } from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import ArrowBack from '@material-ui/icons/ArrowBack';
import IconButton from '@material-ui/core/IconButton';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import {
  addPayerAndSaveInCache,
  recoverRecurringPayment,
  setPayerContactInfo,
  setRecurringPaymentMethod,
} from '../../../../redux/modules/payment';
import PaymentMethodPicker from '../../../../components/PaymentMethodPicker';
import Loader from '../../../../components/Loaders/Loader';
import PayerContactInfoForm from '../../../../components/FormComponents/PayerContactInfoForm/PayerContactInfoForm';
import { MAIN_BUTTON_WIDTH_KEYS } from '../../../../components/MUI/MainButton';
import StepNavigationAmount from '../../../../components/MUI/StepNavigationAmount';
import StepsNavigation from '../../../../components/StepsNavigation';
import MainSwitch from '../../../../components/MUI/MainSwitch';
import FormSection from '../../../../components/FormComponents/FormSection';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import { setDataFormValues } from '../../../../helpers/formInputHelpers';
import { RECURRING_SCHEDULE_PATH } from '../../../../constants/routes';

import useStyles from '../../makeStyles';

import UserInfoIcon from '../../../../img/formSectionIcons/userInfo.svg';
import PaymentInfoIcon from '../../../../img/formSectionIcons/paymentInfo.svg';

const RecurringPaymentStep = ({ goPrevious, blockNextSteps, params }) => {
  const {
    setStepNavigationBox,
    handleOpenMobileSummary,
    generalAmount,
    handlePayClick,
    payBtnTitle,
    payBtnAmount,
    payerContactFormProps,
    isPaymentNextValid,
  } = params;
  const { errors, setValue } = payerContactFormProps;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [urlParams] = useState(queryString.parse(location.hash));
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { user, loadingUser } = useSelector((state) => state.userReducer, shallowEqual);
  const { basket } = useSelector((store) => store.basketReducer, shallowEqual);
  const { payerContactInfo } = useSelector((store) => store.paymentReducer, shallowEqual);
  const { isInsured } = payerContactInfo;
  const { signer } = useSelector((store) => store.recPayment.contract, shallowEqual);

  const {
    payingInProgress,
    recurringPaymentMethod,
    savingRecurringPayment,
    recovering,
    transactionBinding,
  } = useSelector((store) => store.paymentReducer, shallowEqual);

  const classes = useStyles();
  const isDeviceLgSize = useDeviceResize('lg');

  const handleSetIsInsured = useCallback(
    (isSameInsured) => {
      if (isAuthenticated) {
        return;
      }

      let contactInfo;
      if (isSameInsured) {
        contactInfo = {
          ...signer,
          emailConfirm: '',
          isInsured: true,
          isEmailDefault: true,
          isPhoneDefault: true,
        };
      } else {
        contactInfo = {
          firstName: '',
          lastName: '',
          mobilePhone: '',
          email: '',
          emailConfirm: '',
          isInsured: false,
          isEmailDefault: false,
          isPhoneDefault: false,
        };
      }
      dispatch(setPayerContactInfo(contactInfo));
      setDataFormValues(contactInfo, setValue);
    },
    [dispatch, isAuthenticated, setValue, signer],
  );

  useEffect(() => {
    if (urlParams.bfp !== 'true') {
      handleSetIsInsured(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.bfp]);

  useEffect(() => {
    if (basket.length > 1) {
      handleSetIsInsured(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, basket.length]);

  useEffect(() => {
    if (!isEmpty(errors)) {
      blockNextSteps();
    }
  }, [errors, blockNextSteps]);

  useEffect(() => {
    if (urlParams.bfp === 'true') {
      dispatch(recoverRecurringPayment(urlParams.orderId, urlParams.paymentType))
        .then(() => {
          window.location.hash = '';
        })
        .catch(() => {
          history.push(RECURRING_SCHEDULE_PATH);
        });
    }
  }, [urlParams, dispatch, history]);

  useEffect(() => {
    if (isAuthenticated && !loadingUser && user.firstName && !recovering && basket.length > 0) {
      const { email, firstName, lastName, cellPhone, id } = user;
      dispatch(
        addPayerAndSaveInCache({
          email,
          firstName,
          lastName,
          mobilePhone: cellPhone,
          userId: id,
          isPhoneDefault: false,
          isEmailDefault: false,
        }),
      );
    }
  }, [dispatch, isAuthenticated, loadingUser, user, recovering, basket.length]);

  useEffect(() => {
    if (transactionBinding.successRedirectUrl) {
      window.onbeforeunload = null;
      window.location.href = transactionBinding.successRedirectUrl;
    }
  }, [transactionBinding]);

  useEffect(() => {
    setStepNavigationBox(() => (
      <>
        {isDeviceLgSize && (
          <Grid container spacing={2} alignItems="center" onClick={handleOpenMobileSummary}>
            <StepNavigationAmount
              amount={generalAmount}
              iconDown={false}
              isMobile={isDeviceLgSize}
            />
          </Grid>
        )}
        <StepsNavigation
          back={goPrevious}
          next={() => {
            handlePayClick();
          }}
          width={isDeviceLgSize ? MAIN_BUTTON_WIDTH_KEYS.EXTRA_LARGE : ''}
          nextBtnBlock={(
            <>
              {payBtnTitle}
              {!!payBtnAmount && !isDeviceLgSize && (
                <>
                  &nbsp;
                  <b>{payBtnAmount}</b>
                </>
              )}
            </>
          )}
        />
      </>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeviceLgSize, generalAmount]);

  return (
    <>
      {(payingInProgress || savingRecurringPayment || transactionBinding.isLoading) && (
        <Dimmer active inverted>
          <Loader />
        </Dimmer>
      )}

      {!isDeviceLgSize && (
        <IconButton
          onClick={goPrevious}
          className={classes.goBack}
          size="small"
          color="primary"
          type="button"
        >
          <ArrowBack />
          &nbsp;
          <span>{t('insurance.backToContracts')}</span>
        </IconButton>
      )}

      {!isAuthenticated && !(basket.length > 1) && (
        <div className={classes.isInsuredBlock}>
          <MainSwitch
            size="small"
            checked={isInsured}
            onChange={() => handleSetIsInsured(!isInsured)}
          />
          <Typography className={classes.isInsuredText}>
            {t('recurringPayment.isInsured')}
          </Typography>
        </div>
      )}

      {!isAuthenticated && (
        <FormSection title="recurringPayment.steps.payerInfo" icon={UserInfoIcon}>
          <PayerContactInfoForm formProps={payerContactFormProps} />
        </FormSection>
      )}
      <FormSection title="recurringPayment.steps.paymentType" icon={PaymentInfoIcon}>
        <PaymentMethodPicker
          selectedMethod={recurringPaymentMethod}
          selectMethod={(method) => dispatch(setRecurringPaymentMethod(method))}
          savedCards={transactionBinding.savedCards}
          isValid={isPaymentNextValid}
        />
      </FormSection>
    </>
  );
};

RecurringPaymentStep.propTypes = {
  goPrevious: PropTypes.func.isRequired,
  blockNextSteps: PropTypes.func.isRequired,
  params: PropTypes.shape({
    isPaymentNextValid: PropTypes.bool,
    payerContactFormProps: PropTypes.shape({
      register: PropTypes.func,
      errors: PropTypes.shape({}),
      setValue: PropTypes.func,
      formState: PropTypes.shape({}),
    }),
    setStepNavigationBox: PropTypes.func.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    generalAmount: PropTypes.number.isRequired,
    handlePayClick: PropTypes.func.isRequired,
    payBtnTitle: PropTypes.string.isRequired,
    payBtnAmount: PropTypes.string.isRequired,
  }).isRequired,
};

export default RecurringPaymentStep;
